import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.refs.slideshow?.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideChange, { signal });

    this.#autoplayOnLoad();
  }

  #autoplayOnLoad() {
    const playFirstVideo = () => {
      const currentIndex = this.refs.slideshow?.current ?? 0;
      const currentSlide = this.refs.slideshow?.slides?.[currentIndex];
      const deferredMedia = currentSlide?.querySelector('deferred-media[autoplay]');
      if (deferredMedia && typeof deferredMedia.showDeferredMedia === 'function') {
        deferredMedia.showDeferredMedia();

        if (this.refs.slideshow?.autoplay) {
          this.refs.slideshow.pauseAutoplay();
          this.#attachVideoEndedListener(deferredMedia);
        }
      }
    };

    if (document.readyState === 'complete') {
      playFirstVideo();
    } else {
      window.addEventListener('load', playFirstVideo, { once: true });
    }
  }

  #controller = new AbortController();

  #videoEndedController = null;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#videoEndedController?.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  #handleSlideChange = (event) => {
    const { slide, previousIndex } = event.detail;

    this.#videoEndedController?.abort();
    this.#videoEndedController = null;

    const prevSlide = this.refs.slideshow?.slides?.[previousIndex];
    const prevDeferredMedia = prevSlide?.querySelector('deferred-media');
    if (prevDeferredMedia && typeof prevDeferredMedia.pauseMedia === 'function') {
      prevDeferredMedia.pauseMedia();
    }

    const deferredMedia = slide?.querySelector('deferred-media[autoplay]');
    if (deferredMedia && typeof deferredMedia.showDeferredMedia === 'function') {
      deferredMedia.showDeferredMedia();

      if (this.refs.slideshow?.autoplay) {
        this.refs.slideshow.pauseAutoplay();
        this.#attachVideoEndedListener(deferredMedia);
      }
    } else {
      this.refs.slideshow?.resumeAutoplay();
    }
  };

  #attachVideoEndedListener(deferredMedia) {
    this.#videoEndedController = new AbortController();
    const { signal } = this.#videoEndedController;

    const attachListener = () => {
      const video = deferredMedia.querySelector('video');
      if (!video) return false;

      const wasLooping = video.loop;
      if (wasLooping) {
        video.loop = false;
      }

      video.addEventListener('ended', () => {
        if (wasLooping) {
          video.loop = true;
        }
        this.refs.slideshow?.next();
      }, { once: true, signal });

      signal.addEventListener('abort', () => {
        if (wasLooping) {
          video.loop = true;
        }
      });

      return true;
    };

    if (attachListener()) return;

    const observer = new MutationObserver(() => {
      if (attachListener()) {
        observer.disconnect();
      }
    });

    observer.observe(deferredMedia, {
      childList: true,
      subtree: true,
    });

    signal.addEventListener('abort', () => observer.disconnect());
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
